import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { obtenerDetalleFactura } from "../services/facturaService";
import { obtenerEmpresa } from "../services/empresaService";
import { getLogoUrl, getEmpresaId } from "../utils/logoUtils";
import "./FacturaPrint.css";

const mapEmpresa = (e) => {
  const defaults = { nombre: "", direccion: "", rfc: "", telefono: "", regimen: "" };
  if (!e || typeof e !== "object") return defaults;
  const n = Object.fromEntries(Object.entries(e).map(([k, v]) => [k.toLowerCase(), v]));
  const pick = (...keys) => { const f = keys.map(k => k.toLowerCase()).find(k => n[k] != null); return f ? n[f] : undefined; };
  const parts = [
    pick("calle_empresa", "calle"),
    pick("num_ext_empresa", "numext", "num_ext") ? `#${pick("num_ext_empresa", "numext", "num_ext")}` : "",
    pick("colonia_empresa", "colonia"),
    pick("cp_empresa", "cp", "codigo_postal") ? `CP ${pick("cp_empresa", "cp", "codigo_postal")}` : "",
    pick("ciudad_empresa", "ciudad", "municipio"),
    pick("estado_empresa", "estado"),
  ].filter(Boolean);
  return {
    nombre:   pick("nombre_empresa", "nombre", "razon_social", "razonsocial") || "",
    direccion: parts.join(" · "),
    rfc:      pick("rfc_empresa", "rfc") || "",
    telefono: pick("telefonos_empresa", "telefono", "telefono1") || "",
    regimen:  pick("regimen_fiscal", "regimenfiscal", "regimen") || "",
  };
};

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const formatCurrency = (n, currency = "MXN") =>
  num(n).toLocaleString("es-MX", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return String(d);
  }
};

const formatDateTime = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(d);
  }
};

export default function FacturaPrint40() {
  const { serie, factura, empresa } = useParams();

  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [empresaData, setEmpresaData] = useState(mapEmpresa(null));
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    const loadFactura = async () => {
      try {
        setLoading(true);
        setError("");
        const result = await obtenerDetalleFactura({
          serie,
          factura: parseInt(factura, 10),
          empresa: parseInt(empresa, 10),
        });
        if (!cancel) setDetalle(result || null);
      } catch {
        if (!cancel) setError("No se pudo cargar la factura para impresión.");
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    loadFactura();
    return () => { cancel = true; };
  }, [serie, factura, empresa]);

  useEffect(() => {
    const fromLS = JSON.parse(localStorage.getItem("empresa") || "null");
    if (fromLS) { setEmpresaData(mapEmpresa(fromLS)); return; }
    const empId = Number(empresa || localStorage.getItem("empresaId") || 0);
    if (!empId) return;
    (async () => {
      try {
        const resp = await obtenerEmpresa(empId);
        const raw = resp?.data?.data ?? resp?.data ?? resp ?? null;
        const obj = Array.isArray(raw) ? raw[0] : raw;
        if (obj) setEmpresaData(mapEmpresa(obj));
      } catch {}
    })();
  }, [empresa]);

  const computed = useMemo(() => {
    const safeFactura = detalle ?? {};
    const partidas = Array.isArray(safeFactura.partidas) ? safeFactura.partidas : [];
    const moneda = safeFactura.moneda === "D" || safeFactura.moneda === "USD" ? "USD" : "MXN";
    const subtotal = partidas.reduce((s, p) => s + num(p.importe), 0);
    const total = num(safeFactura.importeTotal) || subtotal;
    const iva = total - subtotal;
    return { safeFactura, partidas, moneda, subtotal, total, iva };
  }, [detalle]);

  useEffect(() => {
    if (!loading && detalle) {
      const timer = window.setTimeout(() => window.print(), 500);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [loading, detalle]);

  if (loading) return <div className="print-page-pad">Cargando factura…</div>;
  if (error)   return <div className="print-page-pad print-error">{error}</div>;
  if (!detalle) return <div className="print-page-pad">No se encontró la factura.</div>;

  const { safeFactura, partidas, moneda, subtotal, total, iva } = computed;

  /* ── estilos inline reutilizables ── */
  const S = {
    accentBar:  { height: 4, background: "#1a3e72", borderRadius: 2, margin: "7px 0 8px" },
    infoGrid:   { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0, border: "1px solid #c9d4e8", borderRadius: 6, overflow: "hidden", marginBottom: 10 },
    infoCell:   { padding: "7px 12px", borderRight: "1px solid #c9d4e8" },
    infoCellLast: { padding: "7px 12px" },
    infoLabel:  { fontSize: 9, color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 2 },
    infoValue:  { fontSize: 12, fontWeight: 700, color: "#1a1a2e" },
    folioValue: { fontSize: 18, fontWeight: 800, color: "#1a3e72" },
    th:         { color: "#fff", background: "#1a3e72", padding: "7px 8px", fontSize: 11, fontWeight: 700, border: "none", textAlign: "left" },
    thNum:      { color: "#fff", background: "#1a3e72", padding: "7px 8px", fontSize: 11, fontWeight: 700, border: "none", textAlign: "right" },
    td:         { padding: "6px 8px", borderBottom: "1px solid #eef0f5", fontSize: 11, verticalAlign: "top" },
    tdNum:      { padding: "6px 8px", borderBottom: "1px solid #eef0f5", fontSize: 11, verticalAlign: "top", textAlign: "right", fontVariantNumeric: "tabular-nums" },
    totalsWrap: { display: "grid", gridTemplateColumns: "1fr 230px", border: "1px solid #c9d4e8", borderTop: "none", marginBottom: 10, pageBreakInside: "avoid", breakInside: "avoid" },
    letraBox:   { padding: "9px 12px", borderRight: "1px solid #c9d4e8" },
    letraLabel: { fontSize: 9, color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 },
    letraText:  { fontSize: 11, lineHeight: 1.45, color: "#222" },
    totalsBox:  { padding: "8px 12px", display: "flex", flexDirection: "column", justifyContent: "center" },
    totalRow:   { display: "flex", justifyContent: "space-between", fontSize: 12, padding: "2px 0", borderBottom: "1px solid #eef0f5", color: "#444" },
    totalFinal: { display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 800, padding: "7px 8px", marginTop: 4, background: "#1a3e72", color: "#fff", borderRadius: 4 },
    cfdiCard:   { border: "1px solid #c9d4e8", borderRadius: 6, padding: "10px 12px", marginBottom: 10, background: "#fafbff" },
    cfdiLabel:  { fontSize: 9, color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 2, marginTop: 6 },
    cfdiValue:  { fontSize: 10, color: "#1a1a2e", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", wordBreak: "break-all", lineHeight: 1.4 },
    sectionTitle: { fontSize: 11, fontWeight: 800, color: "#1a3e72", textTransform: "uppercase", letterSpacing: 0.5, margin: "0 0 6px 0", paddingBottom: 4, borderBottom: "2px solid #1a3e72" },
  };

  return (
    <div className="print-page">
      <main className="print-doc">

        {/* ── ENCABEZADO ─────────────────────────────────────── */}
        <div className="print-header">
          <img src={getLogoUrl(getEmpresaId())} alt="Logo" className="print-logo" onError={(e) => { e.target.style.display = "none"; }} />
          <div className="print-header-center">
            <div className="print-empresa-nombre">{empresaData.nombre}</div>
            {empresaData.direccion && (
              <div style={{ fontSize: 10, color: "#555", marginTop: 2 }}>
                {empresaData.direccion}
                {empresaData.telefono && <span style={{ whiteSpace: "nowrap" }}>  ·  Tel: {empresaData.telefono}</span>}
              </div>
            )}
            {(empresaData.rfc || empresaData.regimen) && (
              <div style={{ fontSize: 10, color: "#555", marginTop: 1 }}>
                {empresaData.rfc && <span>RFC: {empresaData.rfc}</span>}
                {empresaData.rfc && empresaData.regimen && <span>  ·  </span>}
                {empresaData.regimen && <span>Régimen: {empresaData.regimen}</span>}
              </div>
            )}
            <div style={{
              display: "inline-block", marginTop: 6,
              padding: "4px 22px", border: "2px solid #1a3e72",
              borderRadius: 3, fontSize: 17, fontWeight: 800,
              color: "#1a3e72", letterSpacing: 3,
            }}>
              FACTURA
            </div>
            <div style={{ fontSize: 10, color: "#6b7280", marginTop: 3 }}>CFDI Versión 4.0</div>
          </div>
          <img src={getLogoUrl(getEmpresaId(), "secundario")} alt="Logo secundario" className="print-logo print-logo-right" onError={(e) => { e.target.style.display = "none"; }} />
        </div>

        {/* Barra de acento */}
        <div style={S.accentBar} />

        {/* ── BARRA SERIE/FOLIO ──────────────────────────────── */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
          <div style={{
            border: "1px solid #c9d4e8", borderRadius: 6, padding: "7px 20px",
            textAlign: "center", background: "#f0f4ff",
          }}>
            <div style={S.infoLabel}>Serie / Folio</div>
            <div style={S.folioValue}>
              {safeFactura.serie}-{safeFactura.numeroDocumento}
            </div>
          </div>
        </div>

        {/* ── DATOS DEL RECEPTOR ─────────────────────────────── */}
        <div style={{ marginBottom: 10 }}>
          <div style={S.sectionTitle}>Datos del Receptor</div>
          <div style={{ ...S.infoGrid, gridTemplateColumns: "2fr 1fr 1fr" }}>
            <div style={{ ...S.infoCell, background: "#f0f4ff" }}>
              <div style={S.infoLabel}>Cliente</div>
              <div style={S.infoValue}>{safeFactura.nombreCliente}</div>
            </div>
            <div style={S.infoCell}>
              <div style={S.infoLabel}>RFC</div>
              <div style={S.infoValue}>{safeFactura.rfc}</div>
            </div>
            <div style={S.infoCellLast}>
              <div style={S.infoLabel}>Fecha</div>
              <div style={S.infoValue}>{formatDate(safeFactura.fechaMovimiento)}</div>
            </div>
          </div>
          <div style={{ ...S.infoGrid, gridTemplateColumns: "2fr 1fr 1fr", borderTop: "none", borderRadius: "0 0 6px 6px" }}>
            <div style={S.infoCell}>
              <div style={S.infoLabel}>Uso CFDI</div>
              <div style={S.infoValue}>{safeFactura.usoCfdi} – {safeFactura.usoCfdiDescripcion}</div>
            </div>
            <div style={S.infoCell}>
              <div style={S.infoLabel}>Método de Pago</div>
              <div style={S.infoValue}>{safeFactura.condicionesPago}</div>
            </div>
            <div style={S.infoCellLast}>
              <div style={S.infoLabel}>Moneda</div>
              <div style={S.infoValue}>{moneda}</div>
            </div>
          </div>
        </div>

        {/* ── CONCEPTOS ──────────────────────────────────────── */}
        <div style={{ marginBottom: 0 }}>
          <div style={S.sectionTitle}>Conceptos</div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #c9d4e8", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "11%" }} />
            <col style={{ width: "38%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "13%" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={S.th}>Clave</th>
              <th style={S.th}>Descripción</th>
              <th style={S.thNum}>Cant.</th>
              <th style={S.th}>Unidad</th>
              <th style={S.thNum}>Precio</th>
              <th style={S.thNum}>Importe</th>
            </tr>
          </thead>
          <tbody>
            {partidas.map((p, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f6f8ff" }}>
                <td style={{ ...S.td, fontWeight: 600 }}>{p.claveArticulo}</td>
                <td style={{ ...S.td, wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "normal" }}>{p.descripcion}</td>
                <td style={S.tdNum}>{num(p.cantidad).toLocaleString("es-MX")}</td>
                <td style={S.td}>
                  {p.unidad}
                  {p.codprodserv && (
                    <div style={{ fontSize: 10, color: "#6b7280" }}>{p.codprodserv}</div>
                  )}
                </td>
                <td style={S.tdNum}>{formatCurrency(p.precio, moneda)}</td>
                <td style={S.tdNum}>{formatCurrency(p.importe, moneda)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── TOTALES ────────────────────────────────────────── */}
        <div style={S.totalsWrap}>
          <div style={S.letraBox}>
            <div style={S.letraLabel}>Importe con letra</div>
            <div style={S.letraText}>{safeFactura.importeEnLetra}</div>
          </div>
          <div style={S.totalsBox}>
            <div style={S.totalRow}>
              <span>Subtotal</span><span>{formatCurrency(subtotal, moneda)}</span>
            </div>
            <div style={S.totalRow}>
              <span>IVA</span><span>{formatCurrency(iva, moneda)}</span>
            </div>
            <div style={S.totalFinal}>
              <span>Total {moneda}</span><span>{formatCurrency(total, moneda)}</span>
            </div>
          </div>
        </div>

        {/* ── DATOS CFDI ─────────────────────────────────────── */}
        <div style={S.sectionTitle}>Datos del CFDI</div>
        <div style={S.cfdiCard}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16 }}>
            <div>
              <div style={S.cfdiLabel}>UUID / Folio Fiscal</div>
              <div style={{ ...S.cfdiValue, fontSize: 11, fontFamily: "inherit", fontWeight: 700, color: "#1a3e72" }}>
                {safeFactura.folioFiscalUuid}
              </div>

              <div style={S.cfdiLabel}>Fecha de Certificación</div>
              <div style={{ ...S.cfdiValue, fontFamily: "inherit", fontSize: 11 }}>
                {formatDateTime(safeFactura.fechaHoraCertificacion)}
              </div>

              <div style={S.cfdiLabel}>Sello CFDI</div>
              <div style={S.cfdiValue}>{safeFactura.selloCfd}</div>

              <div style={S.cfdiLabel}>Sello SAT</div>
              <div style={S.cfdiValue}>{safeFactura.selloSat}</div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minWidth: 150 }}>
              {safeFactura.qrUrl && (
                <>
                  <QRCodeCanvas value={safeFactura.qrUrl} size={130} />
                  <div style={{ fontSize: 9, color: "#6b7280", marginTop: 5, textAlign: "center", wordBreak: "break-all", maxWidth: 140 }}>
                    {safeFactura.folioFiscalUuid}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── PIE DE PÁGINA ──────────────────────────────────── */}
        <div style={{
          borderTop: "2px solid #1a3e72", paddingTop: 7, marginTop: 4,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div style={{ fontSize: 10, color: "#555" }}>
            Este documento es una representación impresa de un CFDI Versión 4.0
            {safeFactura.lugarExpedicionCp && ` · Lugar de expedición: ${safeFactura.lugarExpedicionCp}`}
          </div>
          <div style={{ fontSize: 10, color: "#1a3e72", fontWeight: 700 }}>
            LMD Integradora
          </div>
        </div>

      </main>
    </div>
  );
}
